import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sizes } from '@common/types';
import { formatDate } from '@common/utils';
import { Box, Loader, Pagination, PaginationResults, Table, TableBody, TableHead, Modal, Button } from '@base-app';
import { COMMON_TRANSLATION } from '@constants/index';
import { STUDY_INSIGHTS_TRANSLATION } from '@modules/study-insights/constants';
import { SHARED_TRANSLATION } from '@modules/shared/constants';
import { useSorting } from '@modules/shared/hooks';
import { SampleTrackingTableProps } from './SampleTrackingTable.types';
import { KitFailureReasonResponse, SampleTrackingStatus } from '@modules/study-insights/types';
import {
  StyledTableBodyCell,
  StyledTableBodyRow,
  StyledTableHeadRow,
  StyledNotResultsRowText,
  StyledNotResultsCellText,
  PaginationContainerStyled,
  StyledTableHeadCell,
  StyledCaretUp,
  StyledCaretDown,
  StyledTableHeadDateReceivedCell,
  StyledTableHeadOrderIdCell,
  StyledError,
  StyledViewReasonLink,
} from './SampleTrackingTable.styles';
import { PAGE_SIZE } from '../SampleTracking.types';
import { styled } from '@/stitches.config';
import { useQueryKitFailureReason } from '@/modules/study-insights/hooks';

const PatientCell = styled('td', {
  padding: '$4 $0',
  fontSize: '$4',
  lineHeight: '$6',
  fontWeight: '$bold',
  textAlign: 'left',
  '&:first-child': {
    paddingLeft: '$8',
  },
  '&:last-child': {
    paddingRight: '$8',
  },
});

const ModalHeading = styled('span', {
  margin: 0,
  fontSize: '$5',
  fontWeight: '$bold',
  color: '$gray900',
});

const SampleTrackingTable: FC<SampleTrackingTableProps> = ({
  data,
  isLoading,
  isError,
  offset,
  onChangePage,
  onChangeSorting,
}) => {
  const { t } = useTranslation([STUDY_INSIGHTS_TRANSLATION, SHARED_TRANSLATION]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [firstItemPage, setFirstItemPage] = useState<number>(0);
  const [lastItemPage, setLastItemPage] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedKitId, setSelectedKitId] = useState<string | null>(null);
  const { sorting, handleSorting: setSorting, isColumnSorted, isAscendingSorted } = useSorting();
  const pageSize = PAGE_SIZE;
  const {
    data: kitFailureReasonData,
    isLoading: isLoadingFailureReason,
    refetch,
  } = useQueryKitFailureReason<KitFailureReasonResponse>(
    {
      kitId: selectedKitId ?? '',
    },
    {
      enabled: false,
      cacheTime: 0,
      staleTime: 0,
    },
  );

  const hasMorePages = data?.meta?.hasMorePages;

  const handlePageChange = (page: number): void => {
    const offset = (page - 1) * pageSize;
    onChangePage(offset);
  };

  const handleSorting = (column: string): void => {
    setSorting(column);
    onChangeSorting(sorting);
  };

  const handleViewFailureReason = (kitId: string): void => {
    setSelectedKitId(kitId);
    setIsModalOpen(true);
  };

  useEffect(() => {
    if (selectedKitId && isModalOpen) {
      void refetch();
    }
  }, [selectedKitId, isModalOpen, refetch]);

  const handleCloseModal = (): void => {
    setIsModalOpen(false);
    setSelectedKitId(null);
  };

  useEffect(() => {
    if (data && offset !== undefined) {
      const firstItem = offset + 1;
      let lastItem = 0;
      if (pageSize > data?.data?.length + 1) {
        lastItem = offset + data.data.length;
      } else {
        lastItem = offset + pageSize;
      }
      setFirstItemPage(firstItem);
      setLastItemPage(lastItem);
      setCurrentPage(offset / pageSize + 1);
    }
  }, [pageSize, data, offset]);

  const tableBody =
    !isLoading && data?.data?.length ? (
      data?.data.map((item, index) => (
        <StyledTableBodyRow key={index} css={{ backgroundColor: index % 2 ? '$white' : '$seafoam700' }}>
          <PatientCell className="rb-mask">{item.patientId}</PatientCell>
          <StyledTableBodyCell title={t('sample_tracking.order_id')}>{item.orderId}</StyledTableBodyCell>
          <StyledTableBodyCell>{item.kitId}</StyledTableBodyCell>
          <StyledTableBodyCell title={t('sample_tracking.status_date')}>
            {formatDate(item.statusDate, 'EEEE; MMM d, yyyy')}
          </StyledTableBodyCell>
          <StyledTableBodyCell title={t('sample_tracking.sample_status')}>
            {t(`sample_tracking.status.${item.status.toLowerCase()}`, { ns: STUDY_INSIGHTS_TRANSLATION })}
            {item.status === SampleTrackingStatus.RECOLLECT && (
              <>
                {' - '}
                <StyledViewReasonLink onClick={() => handleViewFailureReason(item.kitId)}>
                  {t(`sample_tracking.failure_reason.view_reason`, { ns: STUDY_INSIGHTS_TRANSLATION })}
                </StyledViewReasonLink>
              </>
            )}
          </StyledTableBodyCell>
        </StyledTableBodyRow>
      ))
    ) : (
      <StyledNotResultsRowText>
        <StyledNotResultsCellText colSpan={6}>
          {isLoading ? (
            <Loader css={{ height: '520px' }} />
          ) : !isError ? (
            t('no_results', { ns: COMMON_TRANSLATION })
          ) : (
            <StyledError>{t('errors.generic', { ns: COMMON_TRANSLATION })}</StyledError>
          )}
        </StyledNotResultsCellText>
      </StyledNotResultsRowText>
    );

  return (
    <>
      <Box css={{ padding: '$4' }}>
        <Table>
          <TableHead>
            <StyledTableHeadRow disabled={!isLoading && !data?.data?.length}>
              <StyledTableHeadCell css={{ cursor: 'auto', width: '15%' }}>
                {t('sample_tracking.patient_id')}
              </StyledTableHeadCell>
              <StyledTableHeadOrderIdCell css={{ width: '15%', cursor: 'auto' }} title={t('sample_tracking.order_id')}>
                {t('sample_tracking.order_id')}
              </StyledTableHeadOrderIdCell>
              <StyledTableHeadCell css={{ width: '15%', cursor: 'auto' }}>
                {t('sample_tracking.kit_id')}
              </StyledTableHeadCell>
              <StyledTableHeadDateReceivedCell
                css={{ width: '20%' }}
                onClick={() => handleSorting('statusDate')}
                title={t('sample_tracking.status_date')}
              >
                {t('sample_tracking.status_date')}
                {isColumnSorted('statusDate') && isAscendingSorted('statusDate') && <StyledCaretUp />}
                {isColumnSorted('statusDate') && !isAscendingSorted('statusDate') && <StyledCaretDown />}
              </StyledTableHeadDateReceivedCell>
              <StyledTableHeadCell
                css={{ width: '25%' }}
                onClick={() => handleSorting('status')}
                title={t('sample_tracking.sample_status')}
              >
                {t('sample_tracking.sample_status')}
                {isColumnSorted('status') && isAscendingSorted('status') && (
                  <StyledCaretUp data-testid="styled-caret-up" />
                )}
                {isColumnSorted('status') && !isAscendingSorted('status') && (
                  <StyledCaretDown data-testid="styled-caret-down" />
                )}
              </StyledTableHeadCell>
            </StyledTableHeadRow>
          </TableHead>
          <TableBody>{tableBody}</TableBody>
        </Table>

        {data?.data != null && data.data.length > 0 && (
          <PaginationContainerStyled>
            <PaginationResults range={[firstItemPage, lastItemPage]} />
            <Pagination
              currentPage={currentPage}
              hasMorePages={hasMorePages}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              continuosPagination={true}
            />
          </PaginationContainerStyled>
        )}
      </Box>

      <Modal
        size={Sizes.SMALL}
        show={isModalOpen}
        onClose={handleCloseModal}
        data-testid="failure-reason-modal"
        title={t('sample_tracking.failure_reason.title', { ns: STUDY_INSIGHTS_TRANSLATION })}
      >
        <Box css={{ padding: '$6', minWidth: '400px' }}>
          {isLoadingFailureReason ? (
            <Box css={{ display: 'flex', justifyContent: 'center', padding: '$8' }}>
              <Loader />
            </Box>
          ) : kitFailureReasonData ? (
            <Box>
              <Box css={{ marginBottom: '$4' }}>
                <ModalHeading>
                  {t(`sample_tracking.failure_reason.kit_id`, { ns: STUDY_INSIGHTS_TRANSLATION })}
                </ModalHeading>{' '}
                {selectedKitId}
              </Box>
              <Box>
                <ModalHeading>
                  {t(`sample_tracking.failure_reason.title`, { ns: STUDY_INSIGHTS_TRANSLATION })}:
                </ModalHeading>{' '}
                {kitFailureReasonData.failureReason}
              </Box>
            </Box>
          ) : (
            <Box>
              <span>
                {t(`sample_tracking.failure_reason.failure_reason_not_registered`, { ns: STUDY_INSIGHTS_TRANSLATION })}
              </span>
            </Box>
          )}
          <Box css={{ marginTop: '$6', display: 'flex', justifyContent: 'flex-end' }}>
            <Button data-testid="close-button" onClick={handleCloseModal}>
              {t('close', { ns: COMMON_TRANSLATION })}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default SampleTrackingTable;
